import { useEffect, useState } from "react";
import { Link } from "wouter";
import { apiClient, ApiError } from "../api/client.js";
import { useMe } from "../api/use-me.js";
import type {
  AdminUser,
  CreateInviteResponse,
  DaemonMachine,
  Invite,
  UserCredentialStatus,
  UserInviteRole,
} from "../api/types.js";

// Vaihe 4.B.3.UM.3 — kayttajahallinta-naekymae (admin-only).
// Listaa olemassa olevat invite:t + lomake uudelle invite:lle.

const ROLE_LABELS: Record<UserInviteRole, string> = {
  user: "Käyttäjä",
  admin: "Admin",
};

function inviteStatus(invite: Invite): { label: string; color: string } {
  if (invite.acceptedAt) {
    return { label: "Hyväksytty", color: "text-emerald-400" };
  }
  if (new Date(invite.expiresAt).getTime() < Date.now()) {
    return { label: "Vanhentunut", color: "text-red-400" };
  }
  return { label: "Odottaa", color: "text-amber-400" };
}

export function AdminKayttajat() {
  const { me, isLoading: meLoading } = useMe();
  const isAdmin = me?.user.roles.includes("admin") ?? false;

  const [invites, setInvites] = useState<Invite[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<UserInviteRole>("user");
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState<CreateInviteResponse | null>(null);
  const [copied, setCopied] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.listInvites();
      setInvites(res.invites);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isAdmin) return;
    void refresh();
  }, [isAdmin]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || creating) return;
    setCreating(true);
    setError(null);
    try {
      const res = await apiClient.createInvite(email.trim(), role);
      setLastCreated(res);
      setEmail("");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  }

  async function copyUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore — käyttäjä kopioi itse
    }
  }

  if (meLoading) {
    return (
      <section className="mx-auto max-w-4xl px-6 py-10">
        <p className="text-slate-400">Ladataan…</p>
      </section>
    );
  }

  if (!isAdmin) {
    return (
      <section className="mx-auto max-w-4xl px-6 py-10">
        <h2 className="text-2xl font-semibold text-slate-100">Käyttäjähallinta</h2>
        <p className="mt-4 text-amber-300">
          Sinulla ei ole oikeuksia tähän näkymään. Vain adminit voivat
          hallinnoida käyttäjiä.{" "}
          <Link href="/" className="text-cyan-400 hover:underline">
            Palaa etusivulle
          </Link>
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-4xl space-y-6 px-6 py-10">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight text-slate-100">
          Käyttäjähallinta
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          Kratti on suljettu ympäristö — kutsu uusia käyttäjiä alta.
          Heille luodaan henkilökohtainen lataus-/kirjautumislinkki.
        </p>
      </header>

      <section className="rounded-lg border border-slate-700 bg-slate-900 p-6">
        <h3 className="text-lg font-medium text-slate-100">Lähetä kutsu</h3>
        <form onSubmit={submit} className="mt-4 grid gap-4 sm:grid-cols-[1fr,auto,auto]">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="kayttaja@example.com"
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-slate-500 focus:outline-none"
            disabled={creating}
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as UserInviteRole)}
            disabled={creating}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
          >
            <option value="user">{ROLE_LABELS.user}</option>
            <option value="admin">{ROLE_LABELS.admin}</option>
          </select>
          <button
            type="submit"
            disabled={creating || !email.trim()}
            className="rounded-md bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
          >
            {creating ? "Luodaan…" : "Luo kutsu"}
          </button>
        </form>

        {lastCreated && (
          <div className="mt-4 rounded-md border border-cyan-700 bg-cyan-950/40 p-4 text-sm">
            <p className="text-cyan-200">
              Kutsu luotu. Lähetä alla oleva linkki uudelle käyttäjälle:
            </p>
            <div className="mt-2 flex gap-2">
              <code className="flex-1 break-all rounded bg-slate-950 px-3 py-2 text-xs text-cyan-100">
                {lastCreated.inviteUrl}
              </code>
              <button
                type="button"
                onClick={() => copyUrl(lastCreated.inviteUrl)}
                className="rounded-md border border-cyan-700 bg-slate-900 px-3 py-2 text-xs text-cyan-100 hover:bg-slate-800"
              >
                {copied ? "Kopioitu ✓" : "Kopioi"}
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Vanhentuu {new Date(lastCreated.expiresAt).toLocaleString("fi-FI")}
            </p>
          </div>
        )}
      </section>

      <UserCredentialsSection />

      <section className="rounded-lg border border-slate-700 bg-slate-900 p-6">
        <h3 className="text-lg font-medium text-slate-100">Aiemmat kutsut</h3>
        {error && (
          <p className="mt-2 rounded border border-red-700 bg-red-950/40 px-3 py-2 text-sm text-red-200">
            {error}
          </p>
        )}
        {isLoading ? (
          <p className="mt-4 text-sm text-slate-500">Ladataan…</p>
        ) : invites.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">Ei vielä kutsuja.</p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-700">
            {invites.map((inv) => {
              const status = inviteStatus(inv);
              return (
                <li
                  key={inv.id}
                  className="grid grid-cols-[1fr,auto,auto] items-center gap-4 py-3 text-sm"
                >
                  <div>
                    <div className="font-medium text-slate-100">{inv.email}</div>
                    <div className="text-xs text-slate-500">
                      {ROLE_LABELS[inv.role as UserInviteRole] ?? inv.role} ·{" "}
                      luotu {new Date(inv.createdAt).toLocaleString("fi-FI")}
                    </div>
                  </div>
                  <span className={`text-xs font-medium ${status.color}`}>
                    {status.label}
                  </span>
                  <span className="text-xs text-slate-500">
                    {inv.acceptedAt
                      ? new Date(inv.acceptedAt).toLocaleString("fi-FI")
                      : `Vanh. ${new Date(inv.expiresAt).toLocaleDateString("fi-FI")}`}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </section>
  );
}

// ADR-020 — per-user Scaleway-provisiointi. Automaatti, ei manuaalisia
// Hetzner Console -klikkauksia. Pool-flow + AddPoolModal poistettu —
// admin painaa per-rivi "Provisioi Scaleway" ja backend tekee ketjun.

function UserCredentialsSection() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [creds, setCreds] = useState<Record<string, UserCredentialStatus>>({});
  const [machinesByUser, setMachinesByUser] = useState<Record<string, DaemonMachine[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [u, c, m] = await Promise.all([
        apiClient.listUsers(),
        apiClient.listUserCredentials(),
        // scope=all → kaikki koneet kaikilta käyttäjiltä (admin-bypass)
        apiClient.listMachines({ scope: "all" }),
      ]);
      setUsers(u.users);
      setCreds(c.users);
      const grouped: Record<string, DaemonMachine[]> = {};
      for (const machine of m.machines) {
        (grouped[machine.userId] ??= []).push(machine);
      }
      setMachinesByUser(grouped);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setError(
          "Per-user-cred-storage ei ole konfiguroitu palvelimella " +
            "(KRATTI_USER_SECRETS_FILE puuttuu .env:stä)."
        );
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function onDelete(email: string) {
    if (
      !confirm(
        `Poista pilvitunnukset käyttäjältä ${email}? ` +
          `Scaleway-bucket + IAM-applikaatio + access-key tuhotaan myös.`
      )
    )
      return;
    setBusyEmail(email);
    try {
      await apiClient.deleteUserCredential(email);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyEmail(null);
    }
  }

  async function onProvisionScaleway(email: string) {
    if (
      !confirm(
        `Provisioi Scaleway-bucket + cred:t käyttäjälle ${email}?\n\n` +
          `Backend luo automaattisesti projektin, IAM-applikaation, ` +
          `policy:n ja access-key:n Scaleway:hin (~30 s).`
      )
    )
      return;
    setBusyEmail(email);
    try {
      await apiClient.provisionScaleway(email);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setError(
          "Scaleway-provisiointi ei käytössä — aseta KRATTI_SCALEWAY_API_TOKEN " +
            "ja KRATTI_SCALEWAY_ORG_ID API:n .env:iin."
        );
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusyEmail(null);
    }
  }

  return (
    <section className="rounded-lg border border-slate-700 bg-slate-900 p-6">
      <h3 className="text-lg font-medium text-slate-100">
        Käyttäjät & pilvitunnukset
      </h3>
      <p className="mt-1 text-sm text-slate-400">
        Provisioi käyttäjälle oma Scaleway-bucket + IAM-cred:t automaattisesti
        (ADR-020). Jokainen käyttäjä saa oman projektin, isolation toteutuu
        bucket-tasolla.
      </p>

      {error && (
        <p className="mt-3 rounded border border-red-700 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      {loading ? (
        <p className="mt-4 text-sm text-slate-500">Ladataan…</p>
      ) : users.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">Ei käyttäjiä.</p>
      ) : (
        <ul className="mt-4 divide-y divide-slate-700">
          {users.map((u) => {
            const cred = creds[u.email];
            const isScaleway = Boolean(cred?.scalewayProjectId);
            const isLegacy = Boolean(cred && !cred.scalewayProjectId);
            const status = isScaleway
              ? { label: "Scaleway ✓", color: "text-emerald-400" }
              : isLegacy
                ? { label: "Hetzner (legacy)", color: "text-amber-400" }
                : { label: "Puuttuu", color: "text-red-400" };
            const machines = machinesByUser[u.id] ?? [];
            const busy = busyEmail === u.email;
            return (
              <li key={u.id} className="py-3 text-sm">
                <div className="grid grid-cols-[1fr,auto,auto,auto] items-center gap-4">
                  <div>
                    <div className="font-medium text-slate-100">{u.email}</div>
                    {u.name && <div className="text-xs text-slate-500">{u.name}</div>}
                  </div>
                  <span className={`text-xs font-medium ${status.color}`}>
                    {status.label}
                  </span>
                  <div className="text-xs text-slate-500 text-right">
                    {cred?.scalewayBucketName && (
                      <div className="font-mono text-slate-300">
                        {cred.scalewayBucketName}
                      </div>
                    )}
                    {cred?.hetznerKeyName && !isScaleway && (
                      <div className="font-mono text-slate-300">
                        {cred.hetznerKeyName}
                      </div>
                    )}
                    <div>
                      {cred?.provisionedAt
                        ? `Provisioitu ${new Date(cred.provisionedAt).toLocaleDateString("fi-FI")}`
                        : cred?.createdAt
                          ? `Luotu ${new Date(cred.createdAt).toLocaleDateString("fi-FI")}`
                          : "—"}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!isScaleway && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onProvisionScaleway(u.email)}
                        title={
                          isLegacy
                            ? "Korvaa Hetzner-cred:t Scaleway:llä (tabula-rasa)"
                            : "Luo Scaleway-projekti + bucket + cred:t"
                        }
                        className="rounded-md border border-cyan-700 bg-cyan-950/40 px-3 py-1 text-xs text-cyan-100 hover:bg-cyan-900/40 disabled:opacity-50"
                      >
                        {busy ? "Provisioidaan…" : "Provisioi Scaleway"}
                      </button>
                    )}
                    {cred && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onDelete(u.email)}
                        className="rounded-md border border-red-700 bg-red-950/40 px-3 py-1 text-xs text-red-200 hover:bg-red-900/40 disabled:opacity-50"
                      >
                        Poista
                      </button>
                    )}
                  </div>
                </div>
                <UserMachinesRow machines={machines} />
              </li>
            );
          })}
        </ul>
      )}

    </section>
  );
}


// Käyttäjäkohtainen rivilista koneista. agentVersion + status + viimeksi
// nähty heartbeat. Jos käyttäjällä ei ole koneita, näytetään hento vihje.
function UserMachinesRow(props: { machines: DaemonMachine[] }) {
  if (props.machines.length === 0) {
    return (
      <div className="mt-1 ml-1 text-xs italic text-slate-600">
        Ei vielä rekisteröityjä koneita.
      </div>
    );
  }
  return (
    <ul className="mt-2 ml-1 space-y-1 text-xs">
      {props.machines.map((m) => (
        <li key={m.id} className="flex items-center gap-3 text-slate-400">
          <span title={`Status: ${m.status}`}>
            {m.status === "online" ? "🟢" : m.status === "offline" ? "⚪" : "🟡"}
          </span>
          <span className="text-slate-200">{m.label || m.hostname || "(nimetön kone)"}</span>
          <span className="font-mono text-slate-500">
            {m.agentVersion ? `agent ${m.agentVersion}` : "agent ?"}
          </span>
          <span className="text-slate-600">
            · viimeksi nähty {new Date(m.lastSeenAt).toLocaleString("fi-FI")}
          </span>
        </li>
      ))}
    </ul>
  );
}

